import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

const DropdownContainer = styled.div`
    position: relative;
    display: inline-block;
`;

const DropdownHeader = styled.div`
    display: flex;
    align-items: center;
    cursor: pointer;
    white-space: nowrap;
`;

const DropdownTitle = styled.span`
    font-size: 16px;
    margin-right: 10px;
`;

/**
 * Menú flotante con placement:
 * - bottom-start / bottom-end: primer nivel (debajo)
 * - right-start / left-start: submenús (derecha/izquierda)
 */
const DropdownMenu = styled.div`
    display: ${(props) => (props.show ? "block" : "none")};
    position: absolute;
    background-color: white;

    min-width: 160px;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);

    /* IMPORTANTE: por encima del resto del header y de otros dropdowns */
    z-index: 2000;

    border-radius: 5px;
    padding: 10px;

    ${(props) => {
        const offset = typeof props.offset === "number" ? props.offset : 8;

        switch (props.placement) {
            case "bottom-start":
                return `
          top: calc(100% + ${offset}px);
          left: 0;
          right: auto;
        `;

            case "bottom-end":
                return `
          top: calc(100% + ${offset}px);
          right: 0;
          left: auto;
        `;

            case "left-start":
                return `
          top: -6px; /* mejora alineación visual con el item */
          right: calc(100% + ${offset}px);
          left: auto;
        `;

            case "right-start":
                return `
          top: -6px; /* mejora alineación visual con el item */
          left: calc(100% + ${offset}px);
          right: auto;
        `;

            default:
                return `
          top: calc(100% + ${offset}px);
          left: 0;
          right: auto;
        `;
        }
    }}
`;

/**
 * Estilo tipo “lista” para items:
 * - robusto ante children complejos (wrappers, nested dropdowns, etc.)
 */
const DropdownItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;

    width: 100%;
    padding: 8px 10px;
    color: black;
    white-space: nowrap;

    & > a {
        display: block;
        color: inherit;
        text-decoration: none;
    }

    &:hover {
        background-color: #f1f1f1;
    }
`;

const OVERFLOW_PX_THRESHOLD_DEFAULT = 300;

export const useDropdown = (
    containerRef,
    delay,
    overflowThreshold = OVERFLOW_PX_THRESHOLD_DEFAULT
) => {
    const [isOpen, setIsOpen] = useState(false);
    const [openToLeft, setOpenToLeft] = useState(false);
    const closeTimeoutRef = useRef(null);

    const calculateDropdownSide = (parentRect, windowWidth) => {
        if (parentRect.right + overflowThreshold > windowWidth) {
            setOpenToLeft(true);
        } else {
            setOpenToLeft(false);
        }
    };

    const handleMouseEnter = () => {
        if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);

        if (containerRef.current && containerRef.current.parentElement) {
            const parentRect = containerRef.current.parentElement.getBoundingClientRect();
            const windowWidth = window.innerWidth;
            calculateDropdownSide(parentRect, windowWidth);
        }

        setIsOpen(true);
    };

    const handleMouseLeave = () => {
        if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);

        closeTimeoutRef.current = setTimeout(() => {
            setIsOpen(false);
        }, delay);
    };

    useEffect(() => {
        return () => {
            if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
        };
    }, [delay]);

    return {
        isOpen,
        openToLeft,
        handleMouseEnter,
        handleMouseLeave,
    };
};

/**
 * placement soportado:
 * - bottom-start (default)
 * - bottom-end
 * - right-start
 * - left-start
 *
 * Compatibilidad:
 * - "after" => right-start
 */
export const FloatingDropdown = ({
                                     children,
                                     delay = 300,
                                     placement = "bottom-start",
                                     offset = 8,
                                 }) => {
    const containerRef = useRef(null);
    const { isOpen, openToLeft, handleMouseEnter, handleMouseLeave } = useDropdown(
        containerRef,
        delay
    );

    const allChildren = React.Children.toArray(children);
    const titleComponent = allChildren.find(({ type }) => type === FloatingDropdown.Title);
    const itemsComponent = allChildren.find(({ type }) => type === FloatingDropdown.Items);

    const hasItems =
        itemsComponent && React.Children.count(itemsComponent.props.children) > 0;

    let computedPlacement = placement === "after" ? "right-start" : placement;

    if (computedPlacement === "right-start" && openToLeft) {
        computedPlacement = "left-start";
    }

    return (
        <DropdownContainer
            ref={containerRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <DropdownHeader>{titleComponent}</DropdownHeader>

            {hasItems && (
                <DropdownMenu show={isOpen} placement={computedPlacement} offset={offset}>
                    {itemsComponent}
                </DropdownMenu>
            )}
        </DropdownContainer>
    );
};

FloatingDropdown.Title = ({ children }) => <DropdownTitle>{children}</DropdownTitle>;

/**
 * Refactor SOLID (mínimo):
 * - wrap=true mantiene comportamiento legacy (categorías/menús existentes)
 * - wrap=false deja el contenido tal cual (ideal para Account)
 */
FloatingDropdown.Items = ({ children, wrap = true }) => {
    const arr = React.Children.toArray(children);

    if (!wrap) return <>{arr}</>;

    return (
        <>
            {arr.map((child, idx) => (
                <DropdownItem key={child?.key ?? idx}>{child}</DropdownItem>
            ))}
        </>
    );
};

/** Esto siver para usar el estilo explicitqamente */
FloatingDropdown.Item = ({ children }) => <DropdownItem>{children}</DropdownItem>;
